import 'bootstrap/dist/css/bootstrap.min.css';
import { Table, Modal, Form, Button, Row, Col } from 'react-bootstrap';
import { useRef, useEffect, useState, useCallback } from 'react';
import $ from 'jquery';
import 'datatables.net-bs5';
import 'datatables.net-responsive-bs5';
import config from '../config';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import * as XLSX from 'xlsx';

const server_URL = config.SERVER_URL;

// Utility function to format date as DD-MM-YYYY, HH:MM:SS am/pm
const formatDateTime = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours % 12 || 12;
  
  return `${day}-${month}-${year}, ${displayHours}:${minutes}:${seconds} ${ampm}`;
};

export function CampaignsTable() {
  const tableRef = useRef(null);
  const [showModal, setShowModal] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [lists, setLists] = useState([]);
  const [servers, setServers] = useState([]);
  const [formData, setFormData] = useState({
    method: 'add',
    campaignName: '',
    subject: '',
    scheduledDateTime: '',
    template_id: '', // Changed from template (file) to template_id (dropdown)
    campaign_email_type: 'list', // 'list', 'tags', or 'segments'
    emailList: '',
    selectedTags: [], // Array of tag IDs when campaign_email_type is 'tags'
    segment_id: '', // Segment ID when campaign_email_type is 'segments'
    server: '',
    mc_preview_text: '',
    customer_id: '',
  });
  const [customers, setCustomers] = useState([]);
  const [userRole, setUserRole] = useState(null);
  const [loggedUserId, setLoggedUserId] = useState(null);
  const [filteredServers, setFilteredServers] = useState([]);
  const [filteredLists, setFilteredLists] = useState([]);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewData, setViewData] = useState({
    server: '',
    campaign_email_type: 'list',
    emailList: '',
    selectedTags: [],
    segment_id: '',
    subject: '',
    message: '',
    emails: [],
    selectedEmails: [],
    campaignId: '',
    mc_preview_text: '',
    customer_id: '',
  });
  const [viewFilteredServers, setViewFilteredServers] = useState([]);
  const [viewFilteredLists, setViewFilteredLists] = useState([]);
  const [isTestMailSending, setIsTestMailSending] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showHtmlSource, setShowHtmlSource] = useState(false);
  const [testLists, setTestLists] = useState([]);
  const [showLogModal, setShowLogModal] = useState(false);
  const [selectedCampaignLogs, setSelectedCampaignLogs] = useState([]);
  const [showArchived, setShowArchived] = useState(false);  // false = showing active campaigns
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: ''
  });
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [selectedCampaignLinks, setSelectedCampaignLinks] = useState([]);
  const [showLinksPage, setShowLinksPage] = useState(false);
  const [customFields, setCustomFields] = useState([]); // Custom fields for selected customer
  const [viewCustomFields, setViewCustomFields] = useState([]); // Custom fields for view modal
  const [selectedCampaignName, setSelectedCampaignName] = useState('');
  const [customerFilter, setCustomerFilter] = useState('');
  const [templates, setTemplates] = useState([]); // Templates for selected customer
  const [tags, setTags] = useState([]); // Tags for selected customer
  const [viewTags, setViewTags] = useState([]); // Tags for view modal
  const [segments, setSegments] = useState([]); // Segments for selected customer
  const [viewSegments, setViewSegments] = useState([]); // Segments for view modal

  const fetchCampaigns = async () => {
    setIsPageLoading(true);
    setIsTableLoading(true);
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const requestBody = { 
        method: 'list',
        show_archived: showArchived,
        user_id: userData.id || null,
        role_id: userData.role_id || null
      };
      
      // For role 2 users, filter by their customer_id
      if (userData.role_id === 2 && userData.id) {
        requestBody.customer_id = userData.id;
      }
      
      const response = await fetch(`${server_URL}/api/campaigns`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      });
      if (!response.ok) throw new Error('Failed to fetch campaigns');
      const data = await response.json();
      if (data.status === '1') {
        setCampaigns(Array.isArray(data.data) ? data.data : []);
        
        // Filter lists based on user role
        let listsData = Array.isArray(data.lists) ? data.lists : [];
        // If role is 2, filter lists to show only logged user's lists
        if (userData.role_id === 2 && userData.id) {
          listsData = listsData.filter(list => list.customer_id === userData.id);
        }
        setLists(listsData);
        
        if (data.servers && !Array.isArray(data.servers) && data.servers[0]) {
          setServers([data.servers[0]]);
        } else {
          setServers(Array.isArray(data.servers) ? data.servers : []);
        }
      } else {
        console.error('Error:', data.error);
        setCampaigns([]);
        setLists([]);
        setServers([]);
      }
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      setCampaigns([]);
      setLists([]);
      setServers([]);
    } finally {
      setIsPageLoading(false);
      setIsTableLoading(false);
    }
  };

  const fetchTestLists = async () => {
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const requestBody = {
        method: 'gettestlists'
      };
      
      // For role 2 users, filter by their customer_id
      if (userData.role_id === 2 && userData.id) {
        requestBody.customer_id = userData.id;
      }
      
      const response = await fetch(`${server_URL}/api/campaigns`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      const data = await response.json();
      if (data.status === '1') {
        let testListsData = Array.isArray(data.data) ? data.data : [];
        // If role is 2, filter test lists to show only logged user's lists
        if (userData.role_id === 2 && userData.id) {
          testListsData = testListsData.filter(list => list.customer_id === userData.id);
        }
        setTestLists(testListsData);
      } else {
        console.error('Failed to fetch test lists:', data.error);
        setTestLists([]);
      }
    } catch (error) {
      console.error('Error fetching test lists:', error);
      setTestLists([]);
    }
  };

  const fetchEmailLogs = async (campaignId) => {
    try {
      const response = await fetch(`${server_URL}/api/campaigns`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          method: 'getEmailLogs',
          campaign_id: campaignId
        }),
      });
      const data = await response.json();
      if (data.status === '1') {
        setSelectedCampaignLogs(data.data);
        setShowLogModal(true);
      } else {
        console.error('Failed to fetch email logs:', data.error);
      }
    } catch (error) {
      console.error('Error fetching email logs:', error);
    }
  };

  const fetchLinkDetails = async (campaignId, campaignName) => {
    setIsPageLoading(true);
    try {
      // Destroy DataTable before switching views
      const $tableElement = $(tableRef.current);
      if ($.fn.DataTable.isDataTable($tableElement)) {
        $tableElement.DataTable().destroy();
      }
      
      const response = await fetch(`${server_URL}/api/links`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          method: 'getLinkDetails',
          campaign_id: campaignId
        }),
      });
      const data = await response.json();
      if (data.status === '1') {
        setSelectedCampaignLinks(data.data);
        setSelectedCampaignName(campaignName);
        setShowLinksPage(true);
      } else {
        console.error('Failed to fetch link details:', data.error);
      }
    } catch (error) {
      console.error('Error fetching link details:', error);
    } finally {
      setIsPageLoading(false);
    }
  };

  const ferchReport = async (campaignId) => {
    // setIsPageLoading(true);
    // alert('Report feature coming soon');
     try {
      const response = await fetch(`${server_URL}/api/campaigns`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          method: 'getCampaignReport',
          campaignId:campaignId
        }),
      });

      const data = await response.json();
      if (data.status === '1') {
        // setTestLists(Array.isArray(data.data) ? data.data : []);
      } else {
        console.error('Failed to fetch report:', data.error);
        // setTestLists([]);
      }
    } catch (error) {
      console.error('Error fetching report:', error);
      // setTestLists([]);
    }

  }

  const handleArchive = useCallback(async (campaignId) => {
    if (!window.confirm('Are you sure to archive? You can unarchive it whenever required from show archive section')) {
      return;
    }
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const response = await fetch(`${server_URL}/api/campaigns`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          method: 'archiveCampaign',
          campaign_id: campaignId,
          is_subuser: userData.is_subuser || false,
          subuser_id: userData.subuser_id || null,
        }),
      });
      const data = await response.json();
      if (data.status === '1') {
        fetchCampaigns(); // Reload the campaigns list
      } else {
        console.error('Failed to archive campaign:', data.error);
      }
    } catch (error) {
      console.error('Error archiving campaign:', error);
    }
  }, [fetchCampaigns]);

  const handleDelete = useCallback(async (campaignId) => {
    if (!window.confirm('Are you sure to delete? You can not undo it once deleted')) {
      return;
    }
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const response = await fetch(`${server_URL}/api/campaigns`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          method: 'deleteCampaign',
          campaign_id: campaignId,
          is_subuser: userData.is_subuser || false,
          subuser_id: userData.subuser_id || null,
        }),
      });
      const data = await response.json();
      if (data.status === '1') {
        fetchCampaigns(); // Reload the campaigns list
      } else {
        console.error('Failed to delete campaign:', data.error);
      }
    } catch (error) {
      console.error('Error deleting campaign:', error);
    }
  }, [fetchCampaigns]);

  const handleUnarchive = useCallback(async (campaignId) => {
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const response = await fetch(`${server_URL}/api/campaigns`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          method: 'unarchiveCampaign',
          campaign_id: campaignId,
          is_subuser: userData.is_subuser || false,
          subuser_id: userData.subuser_id || null,
        }),
      });
      const data = await response.json();
      if (data.status === '1') {
        fetchCampaigns(); // Reload the campaigns list
      } else {
        console.error('Failed to archive campaign:', data.error);
      }
    } catch (error) {
      console.error('Error archiving campaign:', error);
    }
  }, [fetchCampaigns]);

  const handleDateRangeChangeWithArchive = async (archiveValue) => {
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const requestBody = {
        method: 'list',
        show_archived: archiveValue !== undefined ? archiveValue : showArchived,
        start_date: dateRange.startDate,
        end_date: dateRange.endDate,
        user_id: userData.id || null,
        role_id: userData.role_id || null
      };
      
      // Priority: explicit customer filter > role-based filtering
      if (customerFilter !== '' && customerFilter !== null && customerFilter !== undefined) {
        const customerIdValue = String(customerFilter) === '0' ? 0 : parseInt(customerFilter, 10);
        requestBody.customer_id = customerIdValue;
        console.log('Filtering by customer_id:', customerIdValue); // Debug log
      } else if (userData.role_id === 2 && userData.id) {
        // For role 2 users, filter by their customer_id
        requestBody.customer_id = userData.id;
      }
      
      console.log('Request body:', requestBody); // Debug log
      
      const response = await fetch(`${server_URL}/api/campaigns`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });
      const data = await response.json();
      console.log('Response data:', data); // Debug log
      if (data.status === '1') {
        const campaignData = Array.isArray(data.data) ? data.data : [];
        setCampaigns(campaignData);
        
        // Filter lists based on user role
        let listsData = Array.isArray(data.lists) ? data.lists : [];
        // If role is 2, filter lists to show only logged user's lists
        if (userData.role_id === 2 && userData.id) {
          listsData = listsData.filter(list => list.customer_id === userData.id);
        }
        setLists(listsData);
        
        // Update servers
        if (data.servers && !Array.isArray(data.servers) && data.servers[0]) {
          setServers([data.servers[0]]);
        } else {
          setServers(Array.isArray(data.servers) ? data.servers : []);
        }
        
        // Log result for debugging
        if (campaignData.length === 0) {
          console.log('No campaigns found matching the filter criteria');
        } else {
          console.log(`Found ${campaignData.length} campaign(s)`);
        }
      } else {
        console.error('Failed to fetch filtered campaigns:', data.error);
        setCampaigns([]);
        setLists([]);
        setServers([]);
      }
    } catch (error) {
      console.error('Error fetching filtered campaigns:', error);
      setCampaigns([]);
      setLists([]);
      setServers([]);
    }
  };

  const handleDateRangeChange = async () => {
    return handleDateRangeChangeWithArchive(showArchived);
  };

  useEffect(() => {
    fetchCampaigns();
    // Get logged user info
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    setUserRole(userData.role_id || null);
    setLoggedUserId(userData.id || null);
    // If role is 2, set selected customer to logged user
    if (userData.role_id === 2) {
      setFormData(prev => ({ ...prev, customer_id: userData.id }));
    } else {
      // Default to 0 (Common) for role 1 users
      setFormData(prev => ({ ...prev, customer_id: 0 }));
    }
  }, []);

  useEffect(() => {
    if (userRole !== null && loggedUserId !== null) {
      fetchCustomers();
    }
  }, [userRole, loggedUserId]);

  const fetchCustomers = async () => {
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const currentUserRole = userData.role_id || null;
      const currentLoggedUserId = userData.id || null;
      
      const response = await fetch(server_URL + '/api/customers', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          method: 'list',
          user_id: currentLoggedUserId,
          role_id: currentUserRole
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch customers');
      }

      const data = await response.json();
      if (data.status === '1' && data.data) {
        setCustomers(data.data);
        
        // Auto-select if only one customer (for role 2 users)
        if (currentUserRole === 2 && data.data.length === 1) {
          setFormData(prev => ({ ...prev, customer_id: data.data[0].id }));
        }
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const fetchCustomFieldsForCustomer = async (customerId) => {
    if (!customerId || customerId === 0) {
      setCustomFields([]);
      return;
    }
    
    try {
      const response = await fetch(server_URL + '/api/trigger_automations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          method: 'get_custom_fields',
          customer_id: customerId,
        }),
      });

      const data = await response.json();
      if (data.status === '1' && data.data) {
        setCustomFields(data.data || []);
      } else {
        setCustomFields([]);
      }
    } catch (error) {
      console.error('Error fetching custom fields:', error);
      setCustomFields([]);
    }
  };

  const fetchViewCustomFieldsForCustomer = async (customerId) => {
    if (!customerId || customerId === 0) {
      setViewCustomFields([]);
      return;
    }
    
    try {
      const response = await fetch(server_URL + '/api/trigger_automations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          method: 'get_custom_fields',
          customer_id: customerId,
        }),
      });

      const data = await response.json();
      if (data.status === '1' && data.data) {
        setViewCustomFields(data.data || []);
      } else {
        setViewCustomFields([]);
      }
    } catch (error) {
      console.error('Error fetching custom fields:', error);
      setViewCustomFields([]);
    }
  };

  const fetchViewTags = async (customerId) => {
    if (!customerId || customerId === 0) {
      setViewTags([]);
      return;
    }
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const response = await fetch(server_URL + '/api/tags', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          method: 'list',
          customer_id: customerId,
          user_id: userData.id || null,
          role_id: userData.role_id || null,
        }),
      });

      const data = await response.json();
      if (data.status === '1' && data.data) {
        setViewTags(data.data || []);
      } else {
        setViewTags([]);
      }
    } catch (error) {
      console.error('Error fetching tags for view:', error);
      setViewTags([]);
    }
  };

  const fetchViewSegments = async (customerId) => {
    if (!customerId || customerId === 0) {
      setViewSegments([]);
      return;
    }
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const response = await fetch(server_URL + '/api/segments', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          method: 'list',
          customer_id: customerId,
          user_id: userData.id || null,
          role_id: userData.role_id || null,
        }),
      });

      const data = await response.json();
      if (data.status === '1' && data.data) {
        setViewSegments(data.data || []);
      } else {
        setViewSegments([]);
      }
    } catch (error) {
      console.error('Error fetching segments for view:', error);
      setViewSegments([]);
    }
  };

  const fetchTemplates = async (customerId) => {
    if (!customerId || customerId === 0) {
      setTemplates([]);
      return;
    }
    
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const response = await fetch(server_URL + '/api/templates', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          method: 'list',
          customer_id: customerId,
          user_id: userData.id || null,
          role_id: userData.role_id || null,
        }),
      });

      const data = await response.json();
      if (data.status === '1' && data.data) {
        setTemplates(data.data || []);
      } else {
        setTemplates([]);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
      setTemplates([]);
    }
  };

  const fetchTags = async (customerId) => {
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const response = await fetch(server_URL + '/api/tags', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          method: 'list',
          customer_id: customerId,
          user_id: userData.id || null,
          role_id: userData.role_id || null,
        }),
      });

      const data = await response.json();
      if (data.status === '1' && data.data) {
        setTags(data.data || []);
      } else {
        setTags([]);
      }
    } catch (error) {
      console.error('Error fetching tags:', error);
      setTags([]);
    }
  };

  const fetchSegments = async (customerId) => {
    if (!customerId || customerId === 0) {
      setSegments([]);
      return;
    }
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const response = await fetch(server_URL + '/api/segments', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          method: 'list',
          customer_id: customerId,
          user_id: userData.id || null,
          role_id: userData.role_id || null,
        }),
      });

      const data = await response.json();
      if (data.status === '1' && data.data) {
        setSegments(data.data || []);
      } else {
        setSegments([]);
      }
    } catch (error) {
      console.error('Error fetching segments:', error);
      setSegments([]);
    }
  };

  // Filter servers and lists based on selected customer
  useEffect(() => {
    if (formData.customer_id !== '' && formData.customer_id !== null && formData.customer_id !== undefined) {
      const customerId = formData.customer_id;
      const filteredServersList = servers.filter(server => {
        if (customerId === 0) {
          return server.customer_id === 0;
        }
        return server.customer_id === customerId;
      });
      const filteredListsList = lists.filter(list => {
        if (customerId === 0) {
          return list.customer_id === 0;
        }
        return list.customer_id === customerId;
      });
      setFilteredServers(filteredServersList);
      setFilteredLists(filteredListsList);
      
      // Fetch custom fields and tags for selected customer
      if (customerId && customerId !== 0) {
        fetchCustomFieldsForCustomer(customerId);
        fetchTags(customerId);
      } else {
        setCustomFields([]);
        setTags([]);
      }
      
      // Reset server and emailList if they're not in the filtered lists
      if (formData.server && !filteredServersList.find(s => s.id === parseInt(formData.server))) {
        setFormData(prev => ({ ...prev, server: '' }));
      }
      if (formData.emailList && !filteredListsList.find(l => l.id === parseInt(formData.emailList))) {
        setFormData(prev => ({ ...prev, emailList: '' }));
      }
    } else {
      // If no customer selected, show all servers and lists
      setFilteredServers(servers);
      setFilteredLists(lists);
    }
  }, [formData.customer_id, servers, lists]);
  
  // Initialize filtered servers and lists when servers/lists are loaded
  useEffect(() => {
    if (servers.length > 0 || lists.length > 0) {
      if (formData.customer_id !== '' && formData.customer_id !== null && formData.customer_id !== undefined) {
        const customerId = formData.customer_id;
        const filteredServersList = servers.filter(server => {
          if (customerId === 0) {
            return server.customer_id === 0;
          }
          return server.customer_id === customerId;
        });
        const filteredListsList = lists.filter(list => {
          if (customerId === 0) {
            return list.customer_id === 0;
          }
          return list.customer_id === customerId;
        });
        setFilteredServers(filteredServersList);
        setFilteredLists(filteredListsList);
      } else {
        setFilteredServers(servers);
        setFilteredLists(lists);
      }
    }
  }, [servers, lists]);

  useEffect(() => {
    if (!tableRef.current || showLinksPage) return;
    if (userRole === null) return; // Wait for userRole to be set
    
    const $tableElement = $(tableRef.current);
    
    // Destroy existing DataTable instance if it exists
    if ($.fn.DataTable.isDataTable($tableElement)) {
      $tableElement.DataTable().destroy();
      $tableElement.empty();
    }

    // Build columns array conditionally based on user role
    const columns = [
      { 
        data: null, 
        title: 'S.No',
        orderable: true,
        width: '80px',
        className: 'text-center',
        render: function(data, type, row, meta) {
          return meta.row + 1;
        }
      },
      { 
        data: 'name', 
        title: 'Campaign Name', 
        orderable: false,
        render: function(data) {
          if (!data) return '';
          if (data.length > 30) {
            return '<span title="' + data.replace(/"/g, '&quot;') + '">' + data.substring(0, 30) + '...</span>';
          }
          return data;
        }
      },
    ];
    
    // Add Customer column only for role 1 (admin)
    if (userRole === 1) {
      columns.push({
        data: 'customer_username', 
        title: 'Customer', 
        orderable: false,
        render: function(data, type, row) {
          const displayText = data || (row.customer_id === 0 ? 'Common (No Customer)' : 'N/A');
          if (displayText.length > 30) {
            return '<span title="' + displayText.replace(/"/g, '&quot;') + '">' + displayText.substring(0, 30) + '...</span>';
          }
          return displayText;
        }
      });
    }
    
    // Add remaining columns
    columns.push(
      { 
        data: 'subject', 
        title: 'Subject', 
        orderable: false,
        render: function(data) {
          if (!data) return '';
          if (data.length > 30) {
            return '<span title="' + data.replace(/"/g, '&quot;') + '">' + data.substring(0, 30) + '...</span>';
          }
          return data;
        }
      },
        { 
          data: 'schedule_time', 
          title: 'Scheduled Date & Time',
          orderable: false,
          render: function(data) {
            return data ? formatDateTime(data) : '';
          }
        },
        { 
          data: 'status', 
          title: 'Status',
          orderable: false,
          width: '180px',

          render: function(data) {
            const statusMap = {
              'draft': 'Draft',
              'scheduled': 'Scheduled',
              'processing': 'Processing',
              'completed': 'Completed',
              'suspended_daily_limit': 'Daily Limit Reached',
              'error': 'Scheduling',
              'SMTP_server_failed_to_connect': 'SMTP Server Failed to Connect'
            };
            return statusMap[data] || data;
          }
        },
        { 
          data: null, 
          title: 'Action',
          orderable: false,
          width: '250px',
          className: 'text-center action-column',
          render: function(data, type, row) {
            const canSend = ['draft', 'suspended_daily_limit','SMTP_server_failed_to_connect'].includes(row.status);
            const canResend = row.status === 'completed';
            const showArchive = row.archive === 0;

            const actionButtons = `
              <div class="d-flex flex-wrap justify-content-center gap-1 py-2">
                <button class="btn btn-outline-dark btn-sm" data-action="report" data-id="${row.id}" data-name="${row.name}" title="Report">
                  <i class="fas fa-clipboard"></i>
                </button>
               
                ${row.link_count > 0 ? `
                  <button class="btn btn-outline-success btn-sm" data-action="links" data-id="${row.id}" data-name="${row.name}" title="Links Report">
                    <i class="fas fa-link"></i>
                  </button>
                ` : ''}
                ${showArchive ? `
                  <button class="btn btn-outline-primary btn-sm" data-action="archive" data-id="${row.id}" title="Archive">
                    <i class="fas fa-archive"></i>
                  </button>
                ` : `
                  <button class="btn btn-outline-success btn-sm" data-action="unarchive" data-id="${row.id}" title="Unarchive">
                    <i class="fa-solid fa-arrow-up-right-from-square"></i>
                  </button>
                `}
                <button class="btn btn-outline-secondary btn-sm d-none wait-btn" id="wait-btn-${row.id}" title="Sending...">
                  <i class="fas fa-spinner fa-spin"></i>
                </button>
                ${canSend ? `
                  <button class="btn btn-outline-primary btn-sm send-btn" data-action="send" data-id="${row.id}" id="send-btn-${row.id}" title="Send">
                    <i class="fas fa-paper-plane"></i>
                  </button>
                ` : ''}
                ${canResend ? `
                  <button class="btn btn-outline-primary btn-sm resend-btn" data-action="resend" data-id="${row.id}" id="resend-btn-${row.id}" title="Resend">
                    <i class="fa-solid fa-share-from-square"></i>
                  </button>
                ` : ''}
                <button class="btn btn-outline-danger btn-sm" data-action="delete" data-id="${row.id}" title="Delete">
                  <i class="fas fa-trash"></i>
                </button>
                <button class="btn btn-outline-secondary btn-sm" data-action="toggle-actions" data-id="${row.id}" title="Hide Actions">
                  <i class="fas fa-chevron-up"></i>
                </button>
              </div>
            `;

            return `
              <div class="campaign-action-wrapper">
                <div class="d-flex gap-1 flex-wrap justify-content-center">
                  <button class="btn btn-outline-primary btn-sm" data-action="view" data-id="${row.id}" title="View">
                    <i class="fas fa-eye"></i>
                  </button>
                  <button class="btn btn-outline-info btn-sm" data-action="log" data-id="${row.id}" title="Log">
                    <i class="fas fa-history"></i>
                  </button>
                  <button class="btn btn-outline-secondary btn-sm action-toggle" data-action="toggle-actions" data-id="${row.id}" title="More Actions">
                    <i class="fas fa-ellipsis-h"></i>
                  </button>
                </div>
                <div class="campaign-actions d-none" id="action-panel-${row.id}">
                  ${actionButtons}
                </div>
              </div>
            `;
          }
        }
      );
    
    // Function to attach action handlers
    const attachActionHandlers = ($table) => {
      $table.off('click', 'button[data-action]'); // Remove any existing handlers first
      
      // Close all action panels when clicking outside
      $(document).off('click.campaign-actions').on('click.campaign-actions', function(e) {
        if (!$(e.target).closest('.campaign-action-wrapper').length) {
          $('.campaign-actions').addClass('d-none');
          $('.action-toggle i').removeClass('fa-times').addClass('fa-ellipsis-h');
        }
      });
      
      $table.on('click', 'button[data-action]', function(e) {
        e.preventDefault();
        e.stopPropagation();
        const action = $(this).data('action');
        const id = $(this).data('id');
        const name = $(this).data('name');
        
        if (action === 'toggle-actions') {
          e.stopPropagation();
          const panel = document.getElementById(`action-panel-${id}`);
          const toggleBtn = $(this);
          const icon = toggleBtn.find('i');
          
          if (panel) {
            const isHidden = panel.classList.contains('d-none');
            // Close all other panels first
            document.querySelectorAll('.campaign-actions').forEach(el => {
              el.classList.add('d-none');
            });
            // Reset all toggle button icons
            $('.action-toggle i').removeClass('fa-times').addClass('fa-ellipsis-h');
            
            // Toggle the clicked panel
            if (isHidden) {
              // Get button position
              const btnOffset = toggleBtn.offset();
              const btnWidth = toggleBtn.outerWidth();
              const btnHeight = toggleBtn.outerHeight();
              
              // Position panel below the button
              panel.style.top = (btnOffset.top + btnHeight + 5) + 'px';
              panel.style.left = (btnOffset.left + btnWidth / 2) + 'px';
              panel.style.transform = 'translateX(-50%)';
              
              // Check if panel would go off screen and adjust
              const panelWidth = panel.offsetWidth || 200;
              const windowWidth = $(window).width();
              const scrollLeft = $(window).scrollLeft();
              
              if (btnOffset.left + btnWidth / 2 - panelWidth / 2 < scrollLeft) {
                panel.style.left = (btnOffset.left + btnWidth) + 'px';
                panel.style.transform = 'none';
              } else if (btnOffset.left + btnWidth / 2 + panelWidth / 2 > scrollLeft + windowWidth) {
                panel.style.left = btnOffset.left + 'px';
                panel.style.transform = 'none';
              }
              
              panel.classList.remove('d-none');
              icon.removeClass('fa-ellipsis-h').addClass('fa-times');
            } else {
              panel.classList.add('d-none');
              icon.removeClass('fa-times').addClass('fa-ellipsis-h');
            }
          }
          return false;
        }

        // Close action panel when any action is clicked (except toggle)
        const panel = document.getElementById(`action-panel-${id}`);
        if (panel) {
          panel.classList.add('d-none');
          $('.action-toggle i').removeClass('fa-times').addClass('fa-ellipsis-h');
        }
        
        if (action === 'view') {
          ViewMail(id);
        } else if (action === 'send' || action === 'resend') {
          SendMail(id, action);
        } else if (action === 'log') {
          fetchEmailLogs(id);
        } else if (action === 'archive') {
          handleArchive(id);
        } else if (action === 'unarchive') {
          handleUnarchive(id);
        } else if (action === 'delete') {
          handleDelete(id);
        } else if (action === 'links') {
          fetchLinkDetails(id, name);
        } else if (action === 'report') {
          ferchReport(id);
        }
      });
    };

    // Allow DataTable to initialize even with empty data
    const table = $tableElement.DataTable({
      data: campaigns,
      columns: columns,
      columnDefs: [
        {
          targets: -1,
          width: '200px',
          className: 'text-center'
        }
      ],
      responsive: true,
      destroy: true,
      ordering: false,  // Disable sorting for entire table
      scrollX: true,
      autoWidth: false,
      language: {
        emptyTable: "No campaigns found",
        zeroRecords: "No matching campaigns found"
      },
      drawCallback: function() {
        // Reattach event handlers after each table draw
        // This ensures handlers work after DataTable redraws
        attachActionHandlers($tableElement);
      }
    });

    // Attach handlers initially
    attachActionHandlers($tableElement);
    
    // Handle scroll to reposition open panels
    const handleScroll = () => {
      document.querySelectorAll('.campaign-actions:not(.d-none)').forEach(panel => {
        const panelId = panel.id;
        const id = panelId.replace('action-panel-', '');
        const toggleBtn = $(`.action-toggle[data-id="${id}"]`);
        if (toggleBtn.length) {
          const btnOffset = toggleBtn.offset();
          const btnWidth = toggleBtn.outerWidth();
          const btnHeight = toggleBtn.outerHeight();
          
          panel.style.top = (btnOffset.top + btnHeight + 5) + 'px';
          panel.style.left = (btnOffset.left + btnWidth / 2) + 'px';
          panel.style.transform = 'translateX(-50%)';
        }
      });
    };
    
    $(window).on('scroll.campaign-actions resize.campaign-actions', handleScroll);

    // Cleanup function
    return () => {
      if ($.fn.DataTable.isDataTable($tableElement)) {
        $tableElement.DataTable().destroy();
        $tableElement.empty();
      }
      $tableElement.off('click', 'button[data-action]');
      $(document).off('click.campaign-actions');
      $(window).off('scroll.campaign-actions resize.campaign-actions');
    };
  }, [campaigns, showLinksPage, userRole]);

  useEffect(() => {
    if (showViewModal) {
      console.log('Servers data:', servers);
      fetchTestLists();
      // Fetch custom fields when view modal opens and customer is selected
      if (viewData.customer_id && viewData.customer_id !== 0) {
        fetchViewCustomFieldsForCustomer(viewData.customer_id);
      } else {
        setViewCustomFields([]);
      }
    }
  }, [showViewModal, viewData.customer_id]);
  
  // Fetch custom fields and templates when add modal opens and customer is already selected
  useEffect(() => {
    if (showModal && formData.customer_id && formData.customer_id !== 0) {
      fetchCustomFieldsForCustomer(formData.customer_id);
      fetchTemplates(formData.customer_id);
    } else if (showModal && (!formData.customer_id || formData.customer_id === 0)) {
      setCustomFields([]);
      setTemplates([]);
    }
  }, [showModal, formData.customer_id]);

  const handleAddCampaigns = () => {
    setShowModal(true);
    $(`#submit-btn`).removeClass('d-none');
    $(`#loading-btn`).addClass('d-none');
    // Fetch custom fields and templates if customer is already selected
    if (formData.customer_id && formData.customer_id !== 0) {
      fetchCustomFieldsForCustomer(formData.customer_id);
      fetchTemplates(formData.customer_id);
    }
  };
  const handleCloseModal = () => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    const defaultCustomerId = userRole === 2 ? loggedUserId : 0;
    setFormData({
      method: 'add',
      campaignName: '',
      subject: '',
      scheduledDateTime: '',
      template_id: '',
      campaign_email_type: 'list',
      emailList: '',
      selectedTags: [],
      segment_id: '',
      server: '',
      mc_preview_text: '',
      customer_id: defaultCustomerId,
    });
    // Reset filtered lists to show all or based on default customer
    if (defaultCustomerId !== '' && defaultCustomerId !== null && defaultCustomerId !== undefined) {
      const filteredServersList = servers.filter(server => {
        if (defaultCustomerId === 0) {
          return server.customer_id === 0;
        }
        return server.customer_id === defaultCustomerId;
      });
      const filteredListsList = lists.filter(list => {
        if (defaultCustomerId === 0) {
          return list.customer_id === 0;
        }
        return list.customer_id === defaultCustomerId;
      });
      setFilteredServers(filteredServersList);
      setFilteredLists(filteredListsList);
    } else {
      setFilteredServers(servers);
      setFilteredLists(lists);
    }
    setShowModal(false);
  };
  
  const handleInputChange = (e) => {
    const { name, value, type, files } = e.target;
    
    // Special handling for customer_id change
    if (name === 'customer_id') {
      const customerId = value === '' ? '' : (value === '0' ? 0 : Number(value));
      setFormData((prev) => {
        // Filter servers and lists based on selected customer
        let filteredServersList = [];
        let filteredListsList = [];
        
        if (customerId !== '' && customerId !== null && customerId !== undefined) {
          filteredServersList = servers.filter(server => {
            if (customerId === 0) {
              return server.customer_id === 0;
            }
            return server.customer_id === customerId;
          });
          filteredListsList = lists.filter(list => {
            if (customerId === 0) {
              return list.customer_id === 0;
            }
            return list.customer_id === customerId;
          });
        } else {
          filteredServersList = servers;
          filteredListsList = lists;
        }
        
        setFilteredServers(filteredServersList);
        setFilteredLists(filteredListsList);
        
        // Fetch custom fields, templates, tags, and segments for selected customer
        if (customerId && customerId !== 0) {
          fetchCustomFieldsForCustomer(customerId);
          fetchTemplates(customerId);
          fetchTags(customerId);
          fetchSegments(customerId);
        } else {
          setCustomFields([]);
          setTemplates([]);
          setTags([]);
          setSegments([]);
        }
        
        // Reset server, emailList, selectedTags, segment_id, and template_id when customer changes
        return {
          ...prev,
          customer_id: customerId,
          server: '', // Reset server when customer changes
          emailList: '', // Reset emailList when customer changes
          selectedTags: [], // Reset tags when customer changes
          segment_id: '', // Reset segment when customer changes
          template_id: '' // Reset template when customer changes
        };
      });
    } else if (name === 'campaign_email_type') {
      // When campaign_email_type changes, reset emailList, selectedTags, and segment_id
      setFormData((prev) => ({
        ...prev,
        campaign_email_type: value,
        emailList: '', // Reset emailList
        selectedTags: [], // Reset selectedTags
        segment_id: '', // Reset segment_id
      }));
      // If switching to tags, ensure tags are loaded for the current customer
      if (value === 'tags' && formData.customer_id) {
        fetchTags(formData.customer_id);
      }
      // If switching to segments, ensure segments are loaded for the current customer
      if (value === 'segments' && formData.customer_id) {
        fetchSegments(formData.customer_id);
      }
    } else if (name === 'selectedTags') {
      // Handle multiselect tags
      const selectedOptions = Array.from(e.target.selectedOptions, option => parseInt(option.value));
      setFormData((prev) => ({
        ...prev,
        selectedTags: selectedOptions,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: type === 'file' ? files[0] : value,
      }));
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    // Validate customer selection (allow 0 as valid value)
    const customerId = formData.customer_id;
    if (customerId === '' || (customerId !== 0 && (customerId === null || customerId === undefined))) {
      alert('Please select a customer');
      return;
    }
    
    // Validate template selection
    if (!formData.template_id || formData.template_id === '') {
      alert('Please select a template');
      return;
    }
    
    // Fetch the selected template to get html and zip_file_path
    let templateHtml = '';
    let templateZipFilePath = '';
    try {
      const templateResponse = await fetch(`${server_URL}/api/templates`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          method: 'view',
          id: formData.template_id,
        }),
      });
      
      const templateData = await templateResponse.json();
      if (templateData.status === '1' && templateData.data) {
        templateHtml = templateData.data.html || '';
        templateZipFilePath = templateData.data.zipFilePath || '';
      } else {
        alert('Failed to load template data');
        return;
      }
    } catch (error) {
      console.error('Error fetching template:', error);
      alert('Error loading template data');
      return;
    }
    
    const submissionData = new FormData();
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    submissionData.append('user_id', userData.id);
    submissionData.append('method', 'add');
    submissionData.append('campaignName', formData.campaignName); // Backend expects 'campaignName'
    submissionData.append('subject', formData.subject);
    submissionData.append('body', templateHtml); // Set template html to campaigns.body
    submissionData.append('folder_name', templateZipFilePath); // Set template zip_file_path to campaigns.folder_name
    submissionData.append('customer_id', customerId);
    submissionData.append('campaign_email_type', formData.campaign_email_type || 'list');
    submissionData.append('is_subuser', userData.is_subuser || false);
    submissionData.append('subuser_id', userData.subuser_id || '');
    if (formData.campaign_email_type === 'list') {
      submissionData.append('emailList', formData.emailList); // Backend expects 'emailList'
    } else if (formData.campaign_email_type === 'tags') {
      // Send tags as JSON array
      submissionData.append('campaign_tags', JSON.stringify(formData.selectedTags || []));
    } else if (formData.campaign_email_type === 'segments') {
      submissionData.append('segment_id', formData.segment_id);
    }
    submissionData.append('server', formData.server); // Backend expects 'server'
    submissionData.append('mc_preview_text', formData.mc_preview_text || '');
    if (formData.scheduledDateTime) {
      submissionData.append('scheduledDateTime', formData.scheduledDateTime);
    }
    
    // Debug: Log form data
    console.log('Form data being sent:', formData);
    console.log('Template HTML length:', templateHtml.length);
    console.log('Template ZIP file path:', templateZipFilePath);
    console.log('Scheduled DateTime:', formData.scheduledDateTime);
    
    $(`#submit-btn`).addClass('d-none');
    $(`#loading-btn`).removeClass('d-none');
    try {
      const response = await fetch(`${server_URL}/api/campaigns`, {
        method: 'POST',
        body: submissionData,
      });
      const data = await response.json();
      if (data.status == 1) {
        setShowModal(false);
        fetchCampaigns();
        const defaultCustomerId = userRole === 2 ? loggedUserId : 0;
        setFormData({ 
          method: 'add', 
          campaignName: '', 
          subject: '', 
          scheduledDateTime: '', 
          template_id: '',
          campaign_email_type: 'list',
          customer_id: defaultCustomerId,
          emailList: '',
          selectedTags: [],
          server: '',
          mc_preview_text: ''
        });
        // Reset filtered lists based on default customer
        if (defaultCustomerId !== '' && defaultCustomerId !== null && defaultCustomerId !== undefined) {
          const filteredServersList = servers.filter(server => {
            if (defaultCustomerId === 0) {
              return server.customer_id === 0;
            }
            return server.customer_id === defaultCustomerId;
          });
          const filteredListsList = lists.filter(list => {
            if (defaultCustomerId === 0) {
              return list.customer_id === 0;
            }
            return list.customer_id === defaultCustomerId;
          });
          setFilteredServers(filteredServersList);
          setFilteredLists(filteredListsList);
        } else {
          setFilteredServers(servers);
          setFilteredLists(lists);
        }
      } else {
        alert(data.message);
      }
    } catch (error) {
      console.error('Error uploading files:', error);
      alert('An error occurred during file upload');
    } finally {
      $(`#submit-btn`).removeClass('d-none');
      $(`#loading-btn`).addClass('d-none');
    }
  };

  const SendMail = async (id,type) => {
    $(`#${type}-btn-${id}`).addClass('d-none');
    $(`#wait-btn-${id}`).removeClass('d-none');
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const response = await fetch(`${server_URL}/api/campaigns`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          method: 'sendmail',
          id: id,
          is_subuser: userData.is_subuser || false,
          subuser_id: userData.subuser_id || null,
        }),
      });
      const data = await response.json();
      if (data.status == 1) {
        $(`#wait-btn-${id}`).addClass('d-none');
        $(`#resend-btn-${id}`).removeClass('d-none');
        
        // alert(data.message);
      } else {
        $(`#{type}-btn-${id}`).removeClass('d-none');
        $(`#wait-btn-${id}`).addClass('d-none');
        // alert(data.message);
      }

      
    } catch (error) {
      console.error('Error  sending mail:', error);
      $(`#wait-btn-${id}`).addClass('d-none');
      $(`#${type}-btn-${id}`).removeClass('d-none');
    }
  };

  const ViewMail = async (id) => {
    try {
      const response = await fetch(`${server_URL}/api/campaigns`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          method: 'viewcampaign',
          id: id
        }),
      });

      const data = await response.json();
      if (data.status === '1') {
        const campaign = data.data;
        
        // Set all the campaign data
        // Ensure customer_id is a number (0 for Common, or the actual customer ID)
        let finalCustomerId = 0;
        if (campaign.customer_id !== undefined && campaign.customer_id !== null && campaign.customer_id !== '') {
          finalCustomerId = Number(campaign.customer_id);
          // Ensure it's a valid number (not NaN)
          if (isNaN(finalCustomerId)) {
            finalCustomerId = 0;
          }
        } else {
          finalCustomerId = 0;
        }
        
        console.log('ViewMail: Setting customer_id - campaign.customer_id:', campaign.customer_id, 'finalCustomerId:', finalCustomerId);
        
        // Parse campaign_tags if it exists
        let selectedTags = [];
        if (campaign.campaign_tags && Array.isArray(campaign.campaign_tags)) {
          selectedTags = campaign.campaign_tags.map(tagId => parseInt(tagId));
        }
        
        setViewData({
          campaignId: id,
          campaignName: campaign.name,
          subject: campaign.subject,
          server: campaign.server_id,
          campaign_email_type: campaign.campaign_email_type || 'list',
          emailList: campaign.email_list_id,
          selectedTags: selectedTags,
          segment_id: campaign.segment_id || '',
          customer_id: finalCustomerId, // Always a number: 0 for Common, or actual customer ID
          mc_preview_text: campaign.mc_preview_text,
          message: campaign.body || '', // Make sure to get the body field
          scheduledDateTime: campaign.schedule_time ?
            new Date(campaign.schedule_time).toISOString().slice(0, 16) : '',
          selectedEmails: [],
          selectedTestList: ''
        });
        
        // Filter servers and lists based on customer_id
        if (campaign.customer_id !== undefined && campaign.customer_id !== null) {
          const customerId = campaign.customer_id;
          const filteredServersList = servers.filter(server => {
            if (customerId === 0) {
              return server.customer_id === 0;
            }
            return server.customer_id === customerId;
          });
          const filteredListsList = lists.filter(list => {
            if (customerId === 0) {
              return list.customer_id === 0;
            }
            return list.customer_id === customerId;
          });
          setViewFilteredServers(filteredServersList);
          setViewFilteredLists(filteredListsList);
          
          // Fetch tags and segments for view modal if customer is selected
          if (customerId && customerId !== 0) {
            fetchViewTags(customerId);
            fetchViewSegments(customerId);
          } else {
            setViewTags([]);
            setViewSegments([]);
          }
        } else {
          setViewFilteredServers(servers);
          setViewFilteredLists(lists);
          setViewTags([]);
          setViewSegments([]);
        }

        // Set the editor content after a small delay to ensure it's ready
        setTimeout(() => {
          if (window.editor) {
            window.editor.setData(campaign.body || '');
          }
        }, 100);

        // Fetch custom fields and tags for the campaign's customer
        if (finalCustomerId && finalCustomerId !== 0) {
          await fetchViewCustomFieldsForCustomer(finalCustomerId);
          // Fetch tags for view modal
          try {
            const userData = JSON.parse(localStorage.getItem('user') || '{}');
            const tagsResponse = await fetch(server_URL + '/api/tags', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                method: 'list',
                customer_id: finalCustomerId,
                user_id: userData.id || null,
                role_id: userData.role_id || null,
              }),
            });
            const tagsData = await tagsResponse.json();
            if (tagsData.status === '1' && tagsData.data) {
              setViewTags(tagsData.data || []);
            } else {
              setViewTags([]);
            }
            
            // Fetch segments for view modal
            try {
              const segmentsResponse = await fetch(server_URL + '/api/segments', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  method: 'list',
                  customer_id: finalCustomerId,
                  user_id: userData.id || null,
                  role_id: userData.role_id || null,
                }),
              });
              const segmentsData = await segmentsResponse.json();
              if (segmentsData.status === '1' && segmentsData.data) {
                setViewSegments(segmentsData.data || []);
              } else {
                setViewSegments([]);
              }
            } catch (error) {
              console.error('Error fetching segments for view:', error);
              setViewSegments([]);
            }
          } catch (error) {
            console.error('Error fetching tags for view:', error);
            setViewTags([]);
            setViewSegments([]);
          }
        } else {
          setViewCustomFields([]);
          setViewTags([]);
        }
        
        setShowViewModal(true);
        setHasUnsavedChanges(false);
        console.log('Campaign data loaded:', campaign); // For debugging
        console.log('ViewMail: viewData.customer_id set to:', finalCustomerId, 'type:', typeof finalCustomerId);
      } else {
        console.error('Failed to fetch campaign details:', data.error);
      }
    } catch (error) {
      console.error('Error fetching campaign details:', error);
    }
  };

  // Update the editor ready handler
  const handleEditorReady = (editor) => {
    window.editor = editor;
    
    // Set initial content if available
    if (viewData.message) {
      console.log('Setting editor content:', viewData.message); // For debugging
      editor.setData(viewData.message);
    }

    editor.model.document.on('change:data', () => {
      const data = editor.getData();
      console.log('Editor content changed:', data); // For debugging
      setViewData(prev => ({
        ...prev,
        message: data
      }));
      setHasUnsavedChanges(true);
    });
  };

  const handleEmailSelection = (email) => {
    if (email === '') return;
    setViewData(prev => ({
      ...prev,
      selectedEmails: [...new Set([...prev.selectedEmails, email])]
    }));
  };

  const handleTestMail = async () => {
    // Validate test list selection
    if (!viewData.selectedTestList || !viewData.selectedEmails?.length) {
      alert('Please select a test email list before sending test emails');
      return;
    }

    setIsTestMailSending(true);
    try {
      const response = await fetch(`${server_URL}/api/campaigns`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          method: 'testmail',
          server_id: viewData.server,
          emails: viewData.selectedEmails,
          campaign_id: viewData.campaignId,
          subject: viewData.subject,
          test_list_id: viewData.selectedTestList
        }),
      });
      
      const data = await response.json();
      if (data.status === 1) {
        alert('Test emails sent successfully');
      } else {
        alert(data.message || 'Failed to send test emails');
      }
    } catch (error) {
      console.error('Error sending test emails:', error);
      alert('An error occurred while sending test emails');
    } finally {
      setIsTestMailSending(false);
    }
  };

  const handleUpdate = async () => {
    // Implement update functionality
    alert('Update functionality to be implemented');
  };

  const handleTemplateEdit = (e) => {
    // Get the content while preserving line breaks and formatting
    const newContent = e.currentTarget.innerHTML;
    setViewData(prev => ({
      ...prev,
      message: newContent
    }));
    setHasUnsavedChanges(true);
  };

  const handleSaveChanges = async () => {
    try {
      const response = await fetch(`${server_URL}/api/campaigns`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          method: 'updatecampaign',
          campaign_id: viewData.campaignId,
          name: viewData.campaignName,
          server_id: viewData.server,
          campaign_email_type: viewData.campaign_email_type || 'list',
          email_list_id: viewData.campaign_email_type === 'list' ? viewData.emailList : (viewData.campaign_email_type === 'segments' ? null : null),
          segment_id: viewData.campaign_email_type === 'segments' ? viewData.segment_id : null,
          campaign_tags: viewData.campaign_email_type === 'tags' ? viewData.selectedTags : null,
          customer_id: viewData.customer_id,
          subject: viewData.subject,
          body: viewData.message,
          scheduled_date_time: viewData.scheduledDateTime,
          user_id: JSON.parse(localStorage.getItem('user')).id,
          mc_preview_text: viewData.mc_preview_text,
        }),
      });

      const data = await response.json();
      if (data.status === '1') {
        setHasUnsavedChanges(false);
        alert('Campaign saved successfully');
      } else {
        alert(data.error || 'Failed to save campaign');
      }
    } catch (error) {
      console.error('Error saving campaign:', error);
      alert('An error occurred while saving the campaign');
    }
  };

  const handleCloseViewModal = () => {
    if (hasUnsavedChanges) {
      if (window.confirm('You have unsaved changes. Are you sure you want to close?')) {
        setShowViewModal(false);
        setHasUnsavedChanges(false);
      }
    } else {
      setShowViewModal(false);
    }
  };

  const handleTestListChange = async (e) => {
    const selectedListId = e.target.value;
    if (!selectedListId) return;

    try {
      // Fetch emails from the selected test list
      const response = await fetch(`${server_URL}/api/campaigns`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          method: 'gettestlistemails',
          list_id: selectedListId
        }),
      });

      const data = await response.json();
      if (data.status === '1') {
        setViewData(prev => ({
          ...prev,
          selectedTestList: selectedListId,
          selectedEmails: data.data.map(email => email.email)
        }));
      } else {
        alert(data.error || 'Failed to fetch test list emails');
      }
    } catch (error) {
      console.error('Error fetching test list emails:', error);
      alert('An error occurred while fetching test list emails');
    }
  };

  const handleReset = async (campaignId) => {
    if (!window.confirm('Are you sure you want to reset this campaign\'s status?')) {
      return;
    }

    try {
      const response = await fetch(`${server_URL}/api/campaigns`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          method: 'resetcampaign',
          id: campaignId
        }),
      });

      const data = await response.json();
      if (data.status === '1') {
        alert('Campaign status reset successfully');
        fetchCampaigns(); // Refresh the campaigns list
        setShowViewModal(false); // Close the modal
      } else {
        alert(data.message || 'Failed to reset campaign status');
      }
    } catch (error) {
      console.error('Error resetting campaign:', error);
      alert('An error occurred while resetting the campaign');
    }
  };

  const LogModal = ({ show, onHide, logs }) => {
    return (
      <Modal show={show} onHide={onHide} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>Email Logs</Modal.Title>
        </Modal.Header>
        <Modal.Body>
        <Table striped bordered hover style={{ tableLayout: 'fixed', width: '100%' }}>
          <thead>
            <tr style={{ textAlign: 'center' }}>
              <th  className='col-sno'> S.No </th>
              <th>Email</th>
              <th className='col-status'>Status</th>
              <th  className='col-status'>Opened</th>
              <th  className='col-date' >DateTime</th>
            </tr>
          </thead>
            <tbody>
              {logs.map((log, index) => (
                <tr key={log.id}>
                  <td style={{maxWidth: '150px', textAlign: 'center'}}>{index + 1}</td>
                  <td>{log.email_address}</td>
                  <td>{log.status === 1 ? 'Success' : 'Failed'}</td>
                  <td style={{textAlign: 'center'}}>{log.opened}</td>
                  <td>{formatDateTime(log.date_time)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Modal.Body>
      </Modal>
    );
  };

  const LinkModal = ({ show, onHide, links }) => {
    return (
      <Modal show={show} onHide={onHide} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Link Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Table striped bordered hover>
            <thead>
              <tr style={{textAlign: 'center'}}>
                <th style={{maxWidth: '150px'}}>S.No</th>
                <th>Link URL</th>
                <th>Click Count</th>
                <th>Last Clicked</th>
                <th style={{width: '200px'}}>Action</th>
              </tr>
            </thead>
            <tbody>
              {links.map((link, index) => (
                <tr key={index}>
                  <td style={{maxWidth: '150px', textAlign: 'center'}}>{index + 1}</td>
                  <td>
                    <a href={link.url} target="_blank" rel="noopener noreferrer">
                      {link.url}
                    </a>
                  </td>
                  <td style={{textAlign: 'center'}}>{link.click_count || 0}</td>
                  <td>{link.last_clicked ? formatDateTime(link.last_clicked) : 'Never'}</td>
                  <td style={{textAlign: 'center'}}>
                    <div className="d-flex justify-content-center gap-2">
                      <Button
                        variant="link"
                        onClick={() => {
                          navigator.clipboard.writeText(link.url);
                          alert('Link copied to clipboard!');
                        }}
                        title="Copy Link"
                      >
                        <i className="fas fa-copy text-primary"></i>
                      </Button>
                      <Button
                        variant="link"
                        onClick={() => {
                          window.location.href = `${server_URL}/api/campaigns?method=downloadLinkClicks&link_id=${link.id}`;
                        }}
                        title="Download Click Details"
                        disabled={!link.click_count}
                      >
                        <i className="fas fa-download text-success"></i>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
              {links.length === 0 && (
                <tr>
                  <td colSpan="5" className="text-center">No links found</td>
                </tr>
              )}
            </tbody>
          </Table>
        </Modal.Body>
      </Modal>
    );
  };

  const handleBackToCampaigns = () => {
    // Initialize a new DataTable when returning to campaigns view
    setShowLinksPage(false);
    setSelectedCampaignLinks([]);
    setSelectedCampaignName('');
    fetchCampaigns(); // Refresh the campaigns data
  };

  const DownloadClickDetails = async (linkId) => {
    try {
      const response = await fetch(server_URL + '/api/links', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ method: 'downloadLinkClicks', link_id: linkId }),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch click details');
      }

      const data = await response.json();
      
      if (data.status === '1' && data.data.length > 0) {
        // Create workbook
        const workbook = XLSX.utils.book_new();
        
        // Convert data to worksheet format
        const wsData = data.data.map(click => ({
          'email': click.email || '',
          'fname': click.fname || '',
          'lname': click.lname || '',
         
        }));
        
        // Create worksheet
        const ws = XLSX.utils.json_to_sheet(wsData);
        
        // Add worksheet to workbook
        XLSX.utils.book_append_sheet(workbook, ws, 'Link Clicks');
        
        // Generate filename
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
        const filename = `link_clicks_${linkId}_${timestamp}.xlsx`;
        
        // Download file
        XLSX.writeFile(workbook, filename);
      } else {
        alert('No click data found for this link');
      }
    } catch (error) {
      console.error('Error downloading click details:', error);
      alert('Failed to download click details');
    }
  };
  
  return (
    <div className="campaigns-container position-relative" style={{ width: '100%', overflowX: 'hidden' }}>
      {/* Main Page Loader */}
      {isPageLoading && (
        <div 
          className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center"
          style={{
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            zIndex: 9999
          }}
        >
          <div className="text-center">
            <div className="spinner-border text-primary" style={{ width: '3rem', height: '3rem' }} role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <h4 className="mt-3 text-primary">Loading...</h4>
            <p className="text-muted"> we fetch your data</p>
          </div>
        </div>
      )}

      {/* Table Loading Overlay */}
      {isTableLoading && !isPageLoading && (
        <div 
          className="position-absolute w-100 h-100 d-flex justify-content-center align-items-center"
          style={{
            backgroundColor: 'rgba(255, 255, 255, 0.6)',
            zIndex: 999,
            top: 0,
            left: 0
          }}
        >
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Refreshing data...</span>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="container-fluid" style={{ width: '100%', maxWidth: '100%', padding: '1rem' }}>
        {!showLinksPage ? (
          <>
            <div className="mb-3">




              <Row className="mb-4">
                <Col xs={12}>
                  <div className="d-flex flex-wrap justify-content-between align-items-center mb-4">
                    <h3 style={{ 
                      fontSize: '1.75rem', 
                      fontWeight: 700, 
                      color: '#212529',
                      margin: 0
                    }}>
                      Campaigns
                    </h3>
                    <div className="d-flex flex-wrap gap-2">
                      <Button 
                        variant={showArchived ? "secondary" : "primary"}
                        onClick={() => {
                          const newShowArchived = !showArchived;
                          setShowArchived(newShowArchived);
                          // Call filter with the new value immediately
                          handleDateRangeChangeWithArchive(newShowArchived);
                        }}
                        size="sm"
                        style={{ 
                          borderRadius: '0.375rem',
                          fontWeight: 500,
                          padding: '0.375rem 0.75rem'
                        }}
                      >
                        {showArchived ? "Show Active" : "Show Archived"}
                      </Button>
                      <Button 
                        variant="secondary"
                        size="sm"
                        onClick={() => {
                          // If any filters are active, use handleDateRangeChange, otherwise fetchCampaigns
                          if (dateRange.startDate || dateRange.endDate || customerFilter !== '') {
                            handleDateRangeChange();
                          } else {
                            fetchCampaigns();
                          }
                        }}
                        style={{ 
                          borderRadius: '0.375rem',
                          fontWeight: 500,
                          padding: '0.375rem 0.75rem'
                        }}
                      >
                        <i className="fas fa-sync-alt me-1"></i> Refresh
                      </Button>
                      {(dateRange.startDate || dateRange.endDate || customerFilter !== '') && (
                        <Button 
                          variant="warning"
                          size="sm"
                          onClick={() => {
                            setDateRange({ startDate: '', endDate: '' });
                            setCustomerFilter('');
                            fetchCampaigns();
                          }}
                          style={{ 
                            borderRadius: '0.375rem',
                            fontWeight: 500,
                            padding: '0.375rem 0.75rem'
                          }}
                        >
                          <i className="fas fa-times me-1"></i> Clear
                        </Button>
                      )}
                      <Button 
                        variant="primary"
                        size="sm"
                        onClick={handleAddCampaigns}
                        style={{ 
                          borderRadius: '0.375rem',
                          fontWeight: 500,
                          padding: '0.375rem 0.75rem'
                        }}
                      >
                        <i className="fas fa-plus me-1"></i> Add
                      </Button>
                    </div>
                  </div>
                </Col>





                <Col xs={12}>
                  <div 
                    className="d-flex flex-wrap gap-2 align-items-center mb-3"
                    style={{
                      padding: '1rem',
                      backgroundColor: '#f5f5f5',
                      borderRadius: '0.25rem'
                    }}
                  >
                    {/* Customer Filter - Only show for role 1 (admin) users */}
                    {userRole === 1 && (
                      <Form.Group style={{ minWidth: '250px', marginBottom: 0 }}>
                        <Form.Select
                          value={customerFilter}
                          onChange={(e) => setCustomerFilter(e.target.value)}
                          size="sm"
                          style={{
                            height: '40px',
                            backgroundColor: 'white'
                          }}
                        >
                          <option value="">All Customers</option>
                          {/* <option value="0">Common (No Customer)</option> */}
                          {customers.filter(customer => customer.id !== 0).map((customer) => (
                            <option key={customer.id} value={customer.id}>
                              {customer.username} ({customer.email})
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    )}
                    <Form.Group style={{ minWidth: '150px', marginBottom: 0 }}>
                      <Form.Control
                        type="date"
                        size="sm"
                        value={dateRange.startDate}
                        onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
                        style={{
                          height: '40px',
                          backgroundColor: 'white'
                        }}
                      />
                    </Form.Group>
                    <Form.Group style={{ minWidth: '150px', marginBottom: 0 }}>
                      <Form.Control
                        type="date"
                        size="sm"
                        value={dateRange.endDate}
                        onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
                        style={{
                          height: '40px',
                          backgroundColor: 'white'
                        }}
                      />
                    </Form.Group>
                    <Button 
                      variant="primary"
                      onClick={handleDateRangeChange}
                      style={{ 
                        height: '40px',
                        padding: '0.5rem 1rem'
                      }}
                    >
                      Filter
                    </Button>
                    {(dateRange.startDate || dateRange.endDate || customerFilter !== '') && (
                      <Button 
                        variant="outline-warning"
                        onClick={() => {
                          setDateRange({ startDate: '', endDate: '' });
                          setCustomerFilter('');
                          fetchCampaigns();
                        }}
                        style={{ 
                          height: '40px',
                          padding: '0.5rem 1rem'
                        }}
                      >
                        Clear Filter
                      </Button>
                    )}
                    <Button 
                      variant="outline-secondary"
                      onClick={() => {
                        if (dateRange.startDate || dateRange.endDate || customerFilter !== '') {
                          handleDateRangeChange();
                        } else {
                          fetchCampaigns();
                        }
                      }}
                      style={{ 
                        height: '40px',
                        padding: '0.5rem 1rem'
                      }}
                    >
                      Refresh
                    </Button>
                  </div>
                </Col>
                
              </Row>
            </div>
            <div style={{ overflowX: 'auto', width: '100%' }}>
              <Table ref={tableRef} striped bordered hover responsive style={{ marginBottom: 0, tableLayout: 'auto' }}>
                <thead>
                  <tr>
                    <th style={{ minWidth: '80px', width: '80px' }}>S.No</th>
                    <th style={{ minWidth: '200px' }}>Campaign Name</th>
                    {userRole === 1 && <th style={{ minWidth: '200px' }}>Customer</th>}
                    <th style={{ minWidth: '200px' }}>Subject</th>
                    <th style={{ minWidth: '180px' }}>Scheduled Date & Time</th>
                    <th style={{ minWidth: '150px' }}>Status</th>
                    <th style={{ minWidth: '200px', width: '200px' }}>Action</th>
                  </tr>
                </thead>
                <tbody></tbody>
              </Table>
            </div>
          </>
        ) : (
          <div>
              <h3>Link Details </h3>

            <div className="d-flex justify-content-between align-items-center mb-4">
              <h4>Campaign : <span className="text-primary">{selectedCampaignName}</span></h4>
              <div className="d-flex gap-2">
               
               
                <Button 
                  variant="secondary" 
                  onClick={handleBackToCampaigns}
                >
                  <i className="fas fa-arrow-left me-2"></i>
                  Back to Campaigns
                </Button>
              </div>
            </div>
            <Table striped bordered hover>
              <thead>
                <tr style={{textAlign: 'center'}}>
                  <th style={{maxWidth: '150px'}}>S.No</th>
                  <th>Link URL</th>
                  <th>Click Count</th>
                  <th>Last Clicked</th>
                  <th style={{width: '200px'}}>Action</th>
                </tr>
              </thead>
              <tbody>
                {selectedCampaignLinks.map((link, index) => (
                  <tr key={index}>
                    <td style={{maxWidth: '150px', textAlign: 'center'}}>{index + 1}</td>
                    <td>
                      <a href={link.url} target="_blank" rel="noopener noreferrer">
                        {link.url}
                      </a>
                    </td>
                    <td style={{textAlign: 'center'}}>{link.click_count || 0}</td>
                    <td>{link.last_clicked ? formatDateTime(link.last_clicked) : 'Never'}</td>
                    <td style={{textAlign: 'center'}}>
                      <div className="d-flex justify-content-center gap-2">
                        <Button
                          variant="link"
                          onClick={() => {
                            navigator.clipboard.writeText(link.url);
                            alert('Link copied to clipboard!');
                          }}
                          title="Copy Link"
                        >
                          <i className="fas fa-copy text-primary"></i>
                        </Button>
                        <Button
                          variant="link"
                          onClick={() => {DownloadClickDetails(link.id)}}
                          title="Download Click Details"
                          
                        >
                          <i className="fas fa-download text-success"></i>
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {selectedCampaignLinks.length === 0 && (
                  <tr>
                    <td colSpan="5" className="text-center">No links found</td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        )}

        <Modal show={showModal} onHide={handleCloseModal} size="xl">
          <Modal.Header closeButton>
            <Modal.Title>Add Campaign</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form onSubmit={handleSubmit}>
              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label><span className="text-danger">*</span>Campaign Name</Form.Label>
                    <Form.Control type="text" name="campaignName" value={formData.campaignName} onChange={handleInputChange} placeholder="Enter campaign name" />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label><span className="text-danger">*</span>Customer</Form.Label>
                    <Form.Select 
                      name="customer_id" 
                      value={formData.customer_id === 0 ? 0 : (formData.customer_id || 0)} 
                      onChange={handleInputChange}
                      disabled={userRole === 2}
                      required
                    >
                      {/* {userRole === 1 && (
                        <option value={0}>Common (No Customer)</option>
                      )} */}
                       <option value="">Select Customer</option>
                      {userRole === 1 ? (
                        // Role 1: Show all customers
                        customers.map((customer) => (
                          <option key={customer.id} value={customer.id}>
                            {customer.username} ({customer.email})
                          </option>
                        ))
                      ) : (
                        // Role 2: Show only logged user
                        customers.filter(customer => customer.id === loggedUserId).map((customer) => (
                          <option key={customer.id} value={customer.id}>
                            {customer.username} ({customer.email})
                          </option>
                        ))
                      )}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label><span className="text-danger">*</span>Email Server</Form.Label>
                    <Form.Select name="server" value={formData.server} onChange={handleInputChange}>
                      <option value="">Select Server</option>
                      {Array.isArray(filteredServers) && filteredServers.map(server => (
                        <option key={server.id} value={server.id}>
                          {server.name} ({server.email})
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label><span className="text-danger">*</span>List Type</Form.Label>
                    <Form.Select name="campaign_email_type" value={formData.campaign_email_type} onChange={handleInputChange}>
                      <option value="list">List</option>
                      <option value="tags">Tags</option>
                      <option value="segments">Segments</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    {formData.campaign_email_type === 'list' ? (
                      <>
                        <Form.Label><span className="text-danger">*</span>Email List</Form.Label>
                        <Form.Select name="emailList" value={formData.emailList} onChange={handleInputChange}>
                          <option value="">Select Email List</option>
                          {Array.isArray(filteredLists) && filteredLists.map(list => (
                            <option key={list.id} value={list.id}>
                              {list.list_name}
                            </option>
                          ))}
                        </Form.Select>
                      </>
                    ) : formData.campaign_email_type === 'tags' ? (
                      <>
                        <Form.Label><span className="text-danger">*</span>Tags List</Form.Label>
                        <Form.Select 
                          name="selectedTags" 
                          value={formData.selectedTags} 
                          onChange={handleInputChange}
                          multiple
                          size={5}
                        >
                          {Array.isArray(tags) && tags.map(tag => (
                            <option key={tag.id} value={tag.id}>
                              {tag.tag}
                            </option>
                          ))}
                        </Form.Select>
                        <Form.Text className="text-muted">
                          Hold Ctrl/Cmd to select multiple tags
                        </Form.Text>
                      </>
                    ) : (
                      <>
                        <Form.Label><span className="text-danger">*</span>Segments</Form.Label>
                        <Form.Select name="segment_id" value={formData.segment_id} onChange={handleInputChange}>
                          <option value="">Select Segment</option>
                          {Array.isArray(segments) && segments.map(segment => (
                            <option key={segment.id} value={segment.id}>
                              {segment.name}
                            </option>
                          ))}
                        </Form.Select>
                      </>
                    )}
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label><span className="text-danger">*</span>Subject</Form.Label>
                    <Form.Control type="text" name="subject" value={formData.subject} onChange={handleInputChange} placeholder="Enter email subject" />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label><span className="text-danger">*</span>Template</Form.Label>
                    <Form.Select 
                      name="template_id" 
                      value={formData.template_id} 
                      onChange={handleInputChange}
                      disabled={!formData.customer_id || formData.customer_id === 0 || templates.length === 0}
                    >
                      <option value="">{templates.length === 0 ? 'No templates available for this customer' : 'Select Template'}</option>
                      {templates.map((template) => (
                        <option key={template.id} value={template.id}>
                          {template.name}
                        </option>
                      ))}
                    </Form.Select>
                    {(!formData.customer_id || formData.customer_id === 0) && (
                      <Form.Text className="text-muted">
                        Please select a customer first to load templates
                      </Form.Text>
                    )}
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>MC Preview Text</Form.Label>
                    <Form.Control type="text" name="mc_preview_text" value={formData.mc_preview_text} onChange={handleInputChange} placeholder="Enter Text" />
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Scheduled Date & Time</Form.Label>
                    <Form.Control type="datetime-local" name="scheduledDateTime" value={formData.scheduledDateTime} onChange={handleInputChange} />
                  </Form.Group>
                </Col>

              </Row>

              <div className="d-flex justify-content-end gap-2">
                <Button variant="secondary" onClick={handleCloseModal}>Cancel</Button>
                <Button variant="primary" type="submit" id="submit-btn">Save Campaign</Button>
                <Button variant="primary" type="button" id="loading-btn" className="d-none" disabled>
                  <span className="spinner-border spinner-border-sm me-1" role="status"></span>Please Wait
                </Button>
              </div>
            </Form>
          </Modal.Body>
        </Modal>

        <Modal show={showViewModal} onHide={handleCloseViewModal} size="xl">
          <Modal.Header closeButton>
            <Modal.Title>View Campaign</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Row>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>Campaign Name</Form.Label>
                    <Form.Control 
                      type="text" 
                      value={viewData.campaignName || ''} 
                      onChange={(e) => {
                        setViewData(prev => ({
                          ...prev,
                          campaignName: e.target.value
                        }));
                        setHasUnsavedChanges(true);
                      }}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>Customer</Form.Label>
                    <Form.Select 
                      value={
                        viewData.customer_id !== undefined && viewData.customer_id !== null
                          ? Number(viewData.customer_id)
                          : 0
                      }
                      onChange={(e) => {
                        const customerId = e.target.value === '' ? 0 : Number(e.target.value);
                        
                        // Filter servers and lists based on selected customer
                        let filteredServersList = [];
                        let filteredListsList = [];
                        
                        if (customerId !== '' && customerId !== null && customerId !== undefined) {
                          filteredServersList = servers.filter(server => {
                            if (customerId === 0) {
                              return server.customer_id === 0;
                            }
                            return server.customer_id === customerId;
                          });
                          filteredListsList = lists.filter(list => {
                            if (customerId === 0) {
                              return list.customer_id === 0;
                            }
                            return list.customer_id === customerId;
                          });
                        } else {
                          filteredServersList = servers;
                          filteredListsList = lists;
                        }
                        
                        setViewFilteredServers(filteredServersList);
                        setViewFilteredLists(filteredListsList);
                        
                        setViewData(prev => ({
                          ...prev,
                          customer_id: customerId,
                          server: '', // Reset server when customer changes
                          emailList: '', // Reset emailList when customer changes
                          segment_id: '' // Reset segment_id when customer changes
                        }));

                        // Fetch custom fields, tags, and segments when customer changes in view modal
                        if (customerId && customerId !== 0) {
                          fetchViewCustomFieldsForCustomer(customerId);
                          fetchViewTags(customerId);
                          fetchViewSegments(customerId);
                        } else {
                          setViewCustomFields([]);
                          setViewTags([]);
                          setViewSegments([]);
                        }
                        
                        setHasUnsavedChanges(true);
                      }}
                      disabled={userRole === 2}
                    >
                      {/* {userRole === 1 && (
                        <option value={0}>Common (No Customer)</option>
                      )} */}

<option value="">Select Customer</option>

                      {userRole === 1 ? (
                        // Role 1: Show all customers
                        customers.map((customer) => (
                          <option key={customer.id} value={customer.id}>
                            {customer.username} ({customer.email})
                          </option>
                        ))
                      ) : (
                        // Role 2: Show only logged user
                        customers.filter(customer => customer.id === loggedUserId).map((customer) => (
                          <option key={customer.id} value={customer.id}>
                            {customer.username} ({customer.email})
                          </option>
                        ))
                      )}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>Email Server</Form.Label>
                    <Form.Select 
                      value={viewData.server || ''}
                      onChange={(e) => {
                        setViewData(prev => ({
                          ...prev,
                          server: e.target.value
                        }));
                        setHasUnsavedChanges(true);
                      }}
                    >
                      <option value="">Select Server</option>
                      {Array.isArray(viewFilteredServers) && viewFilteredServers.map(server => (
                        <option key={server.id} value={server.id}>
                          {server.name} ({server.email})
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>MC Preview Text</Form.Label>
                    <Form.Control 
                      type="text" 
                      value={viewData.mc_preview_text || ''} 
                      onChange={(e) => {
                        setViewData(prev => ({
                          ...prev,
                          mc_preview_text: e.target.value
                        }));
                        setHasUnsavedChanges(true);
                      }}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>List Type</Form.Label>
                    <Form.Select
                      value={viewData.campaign_email_type || 'list'}
                      onChange={(e) => {
                        setViewData(prev => ({
                          ...prev,
                          campaign_email_type: e.target.value,
                          emailList: e.target.value === 'list' ? prev.emailList : '',
                          selectedTags: e.target.value === 'tags' ? prev.selectedTags : [],
                          segment_id: e.target.value === 'segments' ? prev.segment_id : ''
                        }));
                        setHasUnsavedChanges(true);
                        // Fetch segments if switching to segments type
                        if (e.target.value === 'segments' && viewData.customer_id && viewData.customer_id !== 0) {
                          fetchViewSegments(viewData.customer_id);
                        }
                      }}
                    >
                      <option value="list">List</option>
                      <option value="tags">Tags</option>
                      <option value="segments">Segments</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    {viewData.campaign_email_type === 'list' ? (
                      <>
                        <Form.Label>Email List</Form.Label>
                        <Form.Select
                          value={viewData.emailList || ''}
                          onChange={(e) => {
                            setViewData(prev => ({
                              ...prev,
                              emailList: e.target.value
                            }));
                            setHasUnsavedChanges(true);
                          }}
                        >
                          <option value="">Select List</option>
                          {Array.isArray(viewFilteredLists) && viewFilteredLists.map(list => (
                            <option key={list.id} value={list.id}>
                              {list.list_name}
                            </option>
                          ))}
                        </Form.Select>
                      </>
                    ) : viewData.campaign_email_type === 'tags' ? (
                      <>
                        <Form.Label>Tags List</Form.Label>
                        <Form.Select
                          value={viewData.selectedTags || []}
                          onChange={(e) => {
                            const selectedOptions = Array.from(e.target.selectedOptions, option => parseInt(option.value));
                            setViewData(prev => ({
                              ...prev,
                              selectedTags: selectedOptions
                            }));
                            setHasUnsavedChanges(true);
                          }}
                          multiple
                          size={5}
                        >
                          {Array.isArray(viewTags) && viewTags.map(tag => (
                            <option key={tag.id} value={tag.id}>
                              {tag.tag}
                            </option>
                          ))}
                        </Form.Select>
                        <Form.Text className="text-muted">
                          Hold Ctrl/Cmd to select multiple tags
                        </Form.Text>
                      </>
                    ) : (
                      <>
                        <Form.Label>Segments</Form.Label>
                        <Form.Select
                          value={viewData.segment_id || ''}
                          onChange={(e) => {
                            setViewData(prev => ({
                              ...prev,
                              segment_id: e.target.value
                            }));
                            setHasUnsavedChanges(true);
                          }}
                        >
                          <option value="">Select Segment</option>
                          {Array.isArray(viewSegments) && viewSegments.map(segment => (
                            <option key={segment.id} value={segment.id}>
                              {segment.name}
                            </option>
                          ))}
                        </Form.Select>
                      </>
                    )}
                  </Form.Group>
                </Col>
              </Row>

              <div className="border rounded p-3 mb-4" style={{ backgroundColor: '#f8f9fa' }}>
                <h5 className="mb-3">Email Details</h5>
                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>Subject</Form.Label>
                      <Form.Control
                        type="text"
                        value={viewData.subject || ''}
                        onChange={(e) => {
                          setViewData(prev => ({
                            ...prev,
                            subject: e.target.value
                          }));
                          setHasUnsavedChanges(true);
                        }}
                      />
                    </Form.Group>
                    
                    {/* Display Custom Fields for selected customer */}
                    {viewData.customer_id && viewData.customer_id !== 0 && viewCustomFields.length > 0 && (
                      <div className="mb-3 p-3 bg-info bg-opacity-10 border border-info rounded">
                        <Form.Label className="fw-bold mb-2">Available Custom Fields (use these keys in your template):</Form.Label>
                        <div className="d-flex flex-wrap gap-2 mb-2">
                          {viewCustomFields.map((field, index) => (
                            <span
                              key={index}
                              className="badge bg-light text-dark border border-secondary"
                              style={{ cursor: 'pointer' }}
                              onClick={() => {
                                // Copy key to clipboard
                                navigator.clipboard.writeText(field.key);
                                alert(`Copied ${field.key} to clipboard`);
                              }}
                              title="Click to copy key"
                            >
                              {field.name} ({field.key})
                            </span>
                          ))}
                        </div>
                        <small className="text-muted">
                          Click on any field to copy its key to clipboard. Use these keys in your HTML template (e.g., {viewCustomFields[0]?.key || '[key]'})
                        </small>
                      </div>
                    )}
                  </Col>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>Email Template</Form.Label>
                      <div className="d-flex justify-content-end mb-2">
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => setShowHtmlSource(!showHtmlSource)}
                          className="me-2"
                        >
                          {showHtmlSource ? 'Hide Editor' : 'Show HTML Source'}
                        </Button>
                        <Button 
                          variant="outline-secondary" 
                          size="sm"
                          onClick={() => {
                            const previewWindow = window.open('', '_blank');
                            previewWindow.document.write(viewData.message);
                            previewWindow.document.close();
                          }}
                        >
                          Preview in New Window
                        </Button>
                      </div>
                      {showHtmlSource ? (
                        <Form.Control
                          as="textarea"
                          rows={15}
                          value={viewData.message}
                          onChange={(e) => {
                            setViewData(prev => ({
                              ...prev,
                              message: e.target.value
                            }));
                            setHasUnsavedChanges(true);
                            // Update CKEditor content when switching back
                            if (window.editor) {
                              window.editor.setData(e.target.value);
                            }
                          }}
                          style={{ 
                            fontFamily: 'monospace',
                            fontSize: '14px',
                            whiteSpace: 'pre-wrap'
                          }}
                        />
                      ) : (
                        <div className="border rounded">
                          <CKEditor
                            editor={ClassicEditor}
                            data={viewData.message}
                            onReady={handleEditorReady}
                            onChange={(event, editor) => {
                              const data = editor.getData();
                              setViewData(prev => ({
                                ...prev,
                                message: data
                              }));
                              setHasUnsavedChanges(true);
                            }}
                            config={{
                              toolbar: [
                                'heading',
                                '|',
                                'bold',
                                'italic',
                                'link',
                                'bulletedList',
                                'numberedList',
                                '|',
                                'outdent',
                                'indent',
                                '|',
                                'blockQuote',
                                'insertTable',
                                'undo',
                                'redo'
                              ],
                              height: '400px'
                            }}
                          />
                        </div>
                      )}
                    </Form.Group>
                  </Col>
                </Row>
              </div>

              <div className="border rounded p-3"
                  style={{ backgroundColor: '#f8f9fa' }}
              >
                <h5 className="mb-3">Send Campaign</h5>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Scheduled Date/Time</Form.Label>
                      <Form.Control
                        type="datetime-local"
                        value={viewData.scheduledDateTime || ''}
                        onChange={(e) => {
                          setViewData(prev => ({
                            ...prev,
                            scheduledDateTime: e.target.value
                          }));
                          setHasUnsavedChanges(true);
                        }}
                      />
                    </Form.Group>
                  </Col>
                {userRole === 1 && (
                  <Col md={6}>
                    <div className="border rounded p-3">
                      <h6 className="mb-3">Test Email</h6>
                      <Form.Group className="mb-3">
                        <Form.Label>Test Email List</Form.Label>
                        <Form.Select 
                          value={viewData.selectedTestList}
                          onChange={handleTestListChange}
                        >
                          <option value="">Select Test List</option>
                          {Array.isArray(testLists) && testLists.map(list => (
                            <option key={list.id} value={list.id}>
                              {list.list_name}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                      <Button 
                        variant="primary"
                        onClick={handleTestMail}
                        disabled={isTestMailSending || !viewData.selectedTestList || !viewData.selectedEmails?.length}
                      >
                        {isTestMailSending ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" />
                            Sending...
                          </>
                        ) : (
                          'Send Test Email'
                        )}
                      </Button>
                    </div>
                  </Col>
                )}
                </Row>
              </div>

              <div className="d-flex justify-content-end gap-2 mt-3">
                <Button 
                  variant="warning" 
                  onClick={() => handleReset(viewData.campaignId)}
                  className="me-2"
                >
                  Reset Status
                </Button>
                <Button variant="secondary" onClick={handleCloseViewModal}>
                  Close
                </Button>
                <Button 
                  variant="primary" 
                  onClick={handleSaveChanges} 
                  disabled={!hasUnsavedChanges}
                >
                  Save Changes
                </Button>
              </div>
            </Form>
          </Modal.Body>
        </Modal>

        <LogModal 
          show={showLogModal}
          onHide={() => setShowLogModal(false)}
          logs={selectedCampaignLogs}
        />

        <LinkModal
          show={showLinkModal}
          onHide={() => setShowLinkModal(false)}
          links={selectedCampaignLinks}
        />
      </div>
    </div>
  );
}